import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Save, Download, Printer, Plus, Trash2, FileText } from 'lucide-react';
import API from '@/config/api';

const ContinuingEducationForm = ({ staffId, staffName, staffPosition, hireDate, isOpen, onClose, getAuthHeaders }) => {
  const currentYear = new Date().getFullYear().toString();
  
  const [formData, setFormData] = useState({
    training_year: currentYear,
    hours_worked_per_week: '',
    entries: []
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load existing form data
  useEffect(() => {
    if (isOpen && staffId) {
      loadFormData();
    }
  }, [isOpen, staffId, formData.training_year]);

  const loadFormData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/staff/${staffId}/continuing-education?year=${formData.training_year}`,
        getAuthHeaders()
      );
      if (res.data) {
        setFormData({
          training_year: res.data.training_year || currentYear,
          hours_worked_per_week: res.data.hours_worked_per_week || '',
          entries: res.data.entries || []
        });
      }
    } catch (error) {
      // No existing data, start fresh
      setFormData(prev => ({
        ...prev,
        entries: []
      }));
    } finally {
      setLoading(false);
    }
  };

  const addEntry = () => {
    setFormData(prev => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          id: Date.now().toString(),
          training_date: '',
          training_subject: '',
          sponsor: '',
          trainer_initials: '',
          hours: ''
        }
      ]
    }));
  };

  const updateEntry = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map(entry =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    }));
  };

  const removeEntry = (id) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.filter(entry => entry.id !== id)
    }));
  };

  const calculateTotalHours = () => {
    return formData.entries.reduce((total, entry) => {
      const hours = parseFloat(entry.hours) || 0;
      return total + hours;
    }, 0);
  };

  const saveForm = async () => {
    try {
      setSaving(true);
      await axios.post(
        `${API}/staff/${staffId}/continuing-education`,
        {
          training_year: formData.training_year,
          hours_worked_per_week: formData.hours_worked_per_week,
          entries: formData.entries
        },
        getAuthHeaders()
      );
      toast.success('Continuing Education Record saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const downloadPDF = async () => {
    try {
      // First save, then download
      await saveForm();
      
      const res = await axios.get(
        `${API}/staff/${staffId}/forms/dcf-0053a?training_year=${formData.training_year}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DCF-F-CFS0053A_${staffName?.replace(' ', '_')}_${formData.training_year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const printForm = async () => {
    try {
      // First save, then print
      await saveForm();
      
      const res = await axios.get(
        `${API}/staff/${staffId}/forms/dcf-0053a?training_year=${formData.training_year}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      toast.error('Failed to print form');
    }
  };

  // Generate year options (current year and 5 years back)
  const yearOptions = [];
  for (let i = 0; i < 6; i++) {
    yearOptions.push((parseInt(currentYear) - i).toString());
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            DCF-F-CFS0053A - Continuing Education Record
          </DialogTitle>
          <DialogDescription>
            Track continuing education hours and training for {staffName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Staff Info Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Staff Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Name</Label>
                <p className="font-medium">{staffName}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Position</Label>
                <p className="font-medium">{staffPosition || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Employment Date</Label>
                <p className="font-medium">{hireDate || 'N/A'}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Training Year</Label>
                <Select
                  value={formData.training_year}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, training_year: v }))}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Hours Worked/Week</Label>
                <Input
                  className="h-8"
                  value={formData.hours_worked_per_week}
                  onChange={(e) => setFormData(prev => ({ ...prev, hours_worked_per_week: e.target.value }))}
                  placeholder="e.g., 40"
                />
              </div>
            </CardContent>
          </Card>

          {/* Training Entries Table */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Training Log</CardTitle>
              <Button size="sm" onClick={addEntry} data-testid="add-training-entry">
                <Plus className="w-4 h-4 mr-1" />
                Add Entry
              </Button>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <Table className="mobile-cards">
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-[110px]">Date</TableHead>
                      <TableHead>Training Subject</TableHead>
                      <TableHead className="w-[150px]">Sponsor</TableHead>
                      <TableHead className="w-[80px] text-center">Initials</TableHead>
                      <TableHead className="w-[80px] text-center">Hours</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.entries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No training entries yet. Click "Add Entry" to start.
                        </TableCell>
                      </TableRow>
                    ) : (
                      formData.entries.map((entry, index) => (
                        <TableRow key={entry.id}>
                          <TableCell>
                            <Input
                              type="date"
                              className="h-8 text-sm"
                              value={entry.training_date}
                              onChange={(e) => updateEntry(entry.id, 'training_date', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              className="h-8 text-sm"
                              value={entry.training_subject}
                              onChange={(e) => updateEntry(entry.id, 'training_subject', e.target.value)}
                              placeholder="Training subject"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              className="h-8 text-sm"
                              value={entry.sponsor}
                              onChange={(e) => updateEntry(entry.id, 'sponsor', e.target.value)}
                              placeholder="Sponsor"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              className="h-8 text-sm text-center"
                              value={entry.trainer_initials}
                              onChange={(e) => updateEntry(entry.id, 'trainer_initials', e.target.value)}
                              placeholder="XX"
                              maxLength={4}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              className="h-8 text-sm text-center"
                              value={entry.hours}
                              onChange={(e) => updateEntry(entry.id, 'hours', e.target.value)}
                              placeholder="0"
                              min="0"
                              step="0.5"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeEntry(entry.id)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {/* Total Row */}
                    <TableRow className="bg-muted/30 font-medium">
                      <TableCell colSpan={4} className="text-right">
                        TOTAL HOURS:
                      </TableCell>
                      <TableCell className="text-center text-lg">
                        {calculateTotalHours().toFixed(1)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <div className="flex gap-2 flex-1">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={printForm} data-testid="print-form">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" onClick={downloadPDF} data-testid="download-form">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button onClick={saveForm} disabled={saving} data-testid="save-form">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContinuingEducationForm;
